import type { ReactNode } from "react";
import { Folder as FolderIcon } from "@repo/ui";
import type { NavItemProps } from "@repo/ui";
import { cn } from "@repo/utils";
import type { Brand, Folder, Task } from "~/types/api";

export type NavItemPropsWithMetadata = NavItemProps & {
  _entityId?: string;
  _entityType?: "brand" | "folder" | "task";
};

const iconTextClassName =
  "flex h-6 w-6 items-center justify-center rounded-full text-[8.5px] font-bold";

//Tasks that are not assigned to any folder (orphan tasks)
export const getOrphanTasks = (tasks: Task[], allFolders: Folder[]): Task[] => {
  const tasksInFolders = new Set<number>();

  allFolders.forEach((folder) => {
    folder.folderTasks?.forEach((ft: { taskId: number; folderId: number }) => {
      tasksInFolders.add(ft.taskId);
    });
  });

  return tasks.filter((task) => !tasksInFolders.has(task.id));
};

export const transformTaskToNavItem = (task: Task): NavItemPropsWithMetadata => {
  const last4chars = task.name?.slice(-4).toUpperCase() || "T";

  return {
    label: task.name,
    to: `/dashboard/tasks?openTasks=${task.id}`,
    icon: <div className={cn(iconTextClassName)}>{last4chars}</div>,
    showChevron: false,
    _entityId: `task-${task.id}`,
    _entityType: "task",
  };
};

//Recursive folder tree structure with tasks
export const buildFolderTree = (
  folders: Folder[],
  tasks: Task[],
  expandedIds: Set<string>,
  brandId: number,
  parentId: number | null = null
): NavItemPropsWithMetadata[] => {
  const childFolders = folders.filter((folder) => {
    if (parentId === null) {
      return folder.parentFolderId === null || folder.parentFolderId === undefined;
    }
    return folder.parentFolderId === parentId;
  });

  return childFolders
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((folder) => {
      const entityId = `folder-${brandId}-${folder.id}`;
      const isExpanded = expandedIds.has(entityId);

      const folderTaskIds = new Set(folder.folderTasks?.map((ft) => ft.taskId) || []);

      const folderTasks = tasks.filter((task) => folderTaskIds.has(task.id));
      const childFolderItems = buildFolderTree(folders, tasks, expandedIds, brandId, folder.id);

      const children: NavItemPropsWithMetadata[] = [
        ...folderTasks
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((task) => transformTaskToNavItem(task)),
        ...childFolderItems,
      ];

      return {
        label: folder.name,
        icon: <FolderIcon className="h-5 w-5" />,
        defaultOpen: isExpanded,
        children: children.length > 0 ? (children as ReactNode) : undefined,
        _entityId: entityId,
        _entityType: "folder" as const,
      };
    });
};

//Transform with its children (orphan tasks + folders)
export const transformBrandToNavItem = (
  brand: Brand,
  orphanTasks: Task[],
  folders: Folder[],
  tasks: Task[],
  expandedIds: Set<string>,
  loadingEntities: Set<string>
): NavItemPropsWithMetadata => {
  const entityId = `brand-${brand.id}`;
  const isExpanded = expandedIds.has(entityId);
  const isLoading = loadingEntities.has(entityId);

  const brandIcon = brand.logo ? (
    <img
      src={`data:image/*;base64,${brand.logo}`}
      alt={brand.name}
      className="h-6 w-6 rounded-full object-cover"
    />
  ) : (
    <div
      className={cn(
        "flex h-6 w-6 items-center justify-center rounded-full text-[8.5px] font-bold",
        {
          "text-white": isExpanded,
          "text-blue-600": !isExpanded,
        }
      )}
    >
      {brand.name.substring(0, 5)}
    </div>
  );

  if (isLoading && isExpanded) {
    return {
      label: brand.name,
      icon: brandIcon,
      defaultOpen: isExpanded,
      children: undefined,
      _entityId: entityId,
      _entityType: "brand",
    } as NavItemPropsWithMetadata;
  }

  const folderTree = buildFolderTree(folders, tasks, expandedIds, brand.id, null);
  const children: NavItemPropsWithMetadata[] = [
    ...orphanTasks
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((task) => transformTaskToNavItem(task)),
    ...folderTree,
  ];

  const shouldShowChildren = isExpanded || children.length > 0; //This is for the chevron display
  const hasBeenExpanded = isExpanded || folders.length > 0;
  const shouldShowChevron = hasBeenExpanded ? children.length > 0 : true;

  return {
    label: brand.name,
    icon: brandIcon,
    defaultOpen: isExpanded,
    children: shouldShowChildren ? (children as ReactNode) : undefined,
    showChevron: shouldShowChevron,
    _entityId: entityId,
    _entityType: "brand",
  };
};

//Transform brands to hierarchical structure
export const transformBrandsToSidebarData = (
  brands: Brand[],
  allTasks: Task[],
  foldersByBrand: Map<number, Folder[]>,
  expandedIds: Set<string>,
  loadingEntities: Set<string>
): NavItemPropsWithMetadata[] => {
  return [...brands]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((brand) => {
      const brandTasks = allTasks.filter((task) => task.brandId === brand.id);
      const brandFolders = foldersByBrand.get(brand.id) || [];
      const orphanTasks = getOrphanTasks(brandTasks, brandFolders);

      return transformBrandToNavItem(
        brand,
        orphanTasks,
        brandFolders,
        brandTasks,
        expandedIds,
        loadingEntities
      );
    });
};
